import { applicationPathForStep, companyDriverPath } from './driverDeepLink'

const resolveDriverAppOrigin = () =>
  (process.env.DRIVER_APP_DOMAIN || window.location.origin).replace(/\/$/, '')

const withQuery = (
  path: string,
  query: Record<string, string | null | undefined>
) => {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  const search = params.toString()
  return `${resolveDriverAppOrigin()}${path}${search ? `?${search}` : ''}`
}

export interface DriverLinkOptions {
  companySlug?: string | null
  applicationId?: string | null
  phone?: string | null
  step?: string | null
}

export const buildDriverLink = ({
  companySlug,
  applicationId,
  phone,
  step,
}: DriverLinkOptions) =>
  companySlug
    ? withQuery(companyDriverPath(companySlug, step), { phone })
    : withQuery(applicationPathForStep(step), { applicationId, phone })

export const buildApplicationUrl = (applicationId: string) =>
  buildDriverLink({ applicationId })

export const buildCompanyApplicationUrl = (companySlug: string) =>
  buildDriverLink({ companySlug })

export const buildTrackingLinkUrl = (uniqueLink: string) => {
  return `${resolveDriverAppOrigin()}/short-form/${uniqueLink}`
}

export const buildJobBoardUrl = (companyId: string) => {
  return `${resolveDriverAppOrigin()}/public-short-form/${companyId}`
}

// Links copied out of PDFs/Word docs can turn "ff" into a ligature glyph:
// the real ligature (U+FB00), or a font glyph id that surfaces as U+01AF "Ư"
// or the private-use U+1001AF. Seen in prod as `2%C6%AFda8%C6%AF1ef0-...`.
const LIGATURE_REPLACEMENTS: Array<[RegExp, string]> = [
  [/%C6%AF/gi, 'ff'],
  [/%ED%AF%80%ED%B6%AF/gi, 'ff'],
  [/%F4%80%86%AF/gi, 'ff'],
  [/Ư|\u{1001AF}|ﬀ/gu, 'ff'],
  [/ﬁ/g, 'fi'],
  [/ﬂ/g, 'fl'],
  [/ﬃ/g, 'ffi'],
  [/ﬄ/g, 'ffl'],
]

/**
 * Repairs a UUID path param mangled by copy/paste (ligatures, dropped
 * hyphens). Returns the input unchanged if it can't be repaired.
 */
export const normalizeUuidPathParam = (param: string): string => {
  if (!param) return param

  let value = param
  for (const [pattern, replacement] of LIGATURE_REPLACEMENTS) {
    value = value.replace(pattern, replacement)
  }
  try {
    value = decodeURIComponent(value)
  } catch {
    // Leave undecodable input as-is
  }
  for (const [pattern, replacement] of LIGATURE_REPLACEMENTS) {
    value = value.replace(pattern, replacement)
  }
  value = value.trim().toLowerCase()

  const hex = value.replace(/-/g, '')
  if (!/^[0-9a-f]{32}$/.test(hex)) return param

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-')
}

export const buildZipCodeMapsUrl = (zipCode: string) => {
  return `https://www.google.com/maps/search/?api=1&query=zip+code+${encodeURIComponent(
    zipCode
  )}`
}

export const buildFmcsaCarrierUrl = (dotNumber: string) => {
  return `https://safer.fmcsa.dot.gov/query.asp?query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${encodeURIComponent(
    dotNumber
  )}`
}
